import { useQueryClient } from "react-query"
import * as Yup from "yup"
import { useNdfDetailsSalarieContext } from "../../../NdfDetails/salarie/NdfDetailsSalarie"
import { useState } from "react"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR, CONST_NDF_DETAILS_CATEGORIE_KM, CONST_NDF_DETAILS_CATEGORIE_REPAS_MIDI } from "@common-constants/*"
import { INdfAddNdfDetailRequestModel } from "../../../../../core/_models"
import { Form, Formik, FormikHelpers } from "formik"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import { ndfAddDepenseSimple } from "../../../../../core/_request"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { IConsultantNdfDetailModel, IHttpErrorResponseModel } from "@common-models/*"
import MyAlert from "@common-utils/MyAlert"
import clsx from "clsx"
import { NdfBasicFieldsSection } from "./components/NdfBasicFieldsSection"
import { NdfMealSection } from "./components/NdfMealSection"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import { NdfMileageSection } from "./components/NdfMileageSection"
import { NdfCommentSection } from "./components/NdfCommentSection"
import { NdfSubmitActions } from "./components/NdfSubmitActions"
import { NdfJustificatifsSection } from "./components/NdfJustificatifsSection"

export const NdfUpdateDetailsWrapperFormAddDepenseSimple = () => {
     const queryClient = useQueryClient()
     const context = useNdfDetailsSalarieContext()
     const [actionAfterSubmitForm, setActionAfterSubmitForm] = useState<"close" | "keepAddingDetails">("close")

     const [detailsIndemniteKm, setDetailsIndemniteKm] = useState<{
          distanceEnKmEntreDepartEtDestination: number
          distanceTotal: number
          montantTTC: number
     }>({ distanceEnKmEntreDepartEtDestination: 0, distanceTotal: 0, montantTTC: 0 })

     // Schema
     const validationSchema = Yup.object().shape({
          categorie: Yup.string().label("Catégorie").required("Champ requis"),

          date: Yup.string()
               .label("Date")
               .when("categorie", {
                    is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Champ requis"),
                    otherwise: schema => schema.notRequired(),
               }),

          montantTTC: Yup.number()
               .label("Montant TTC")
               .when("categorie", {
                    is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),

          description: Yup.string()
               .label("Description")
               .when("categorie", {
                    is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Champ requis"),
                    otherwise: schema => schema.notRequired(),
               }),

          documents: Yup.array()
               .label("Justificatifs")
               .of(Yup.mixed())
               .when("categorie", {
                    is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema =>
                         schema
                              .of(
                                   Yup.mixed()
                                        .test("fileSize", "La taille de la PJ doit être inférieure ou égale à 10MB", (value: any) => !value || value.size <= 10000000)
                                        .test(
                                             "fileFormat",
                                             "La PJ doit être au format JPEG, PNG ou PDF",
                                             (value: any) => !value || ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
                                        )
                              )
                              .min(1, "Au moins un document est requis"),
                    otherwise: schema => schema.notRequired(), // Les documents sont bien optionnels ici
               }),

          repasSeulOuEnGroupe: Yup.string()
               .label(`"Seul ou en groupe"`)
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
                    then: schema => schema.required(),
               }),

          invites: Yup.array()
               .label(`"Invités"`)
               .when("repasSeulOuEnGroupe", {
                    is: "REPAS_MIDI_EN_GROUPE",
                    then: schema => schema.required(),
               }),

          adresseDepart: Yup.string()
               .label("Adresse de départ")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),

          adresseArrive: Yup.string()
               .label("Adresse d'arrivée")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),

          nbTrajets: Yup.number()
               .label("Nombre de trajets")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),

          distanceTotale: Yup.number()
               .label("Distance totale")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required(),
               }),

          isAllerRetour: Yup.boolean().label("Aller-retour"),

          moisAnneeKm: Yup.string()
               .label("Mois du trajet")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema => schema.required("Le mois est requis"),
               }),

          joursKm: Yup.array()
               .label("Jours sélectionnés")
               .when("categorie", {
                    is: CONST_NDF_DETAILS_CATEGORIE_KM,
                    then: schema =>
                         schema
                              .min(1, "Veuillez sélectionner au moins un jour dans l'agenda")
                              .test("exact-jours", "Le nombre de jours sélectionnés doit correspondre au nombre de trajets.", function (val) {
                                   const nbTrajets = this.parent.nbTrajets || 0
                                   if (nbTrajets === 0) return true
                                   return val && val.length === nbTrajets
                              }),
               }),
     })

     const initialValues: INdfAddNdfDetailRequestModel = {
          documents: [new File([new Blob()], "")],
          isAllerRetour: false,
          categorie: "",
          moisAnneeKm: "",
          joursKm: [],
     }

     function handleSubmit(values: INdfAddNdfDetailRequestModel, helpers: FormikHelpers<INdfAddNdfDetailRequestModel>) {
          helpers.setStatus(null)

          FileAndDriveHelper.processFiles(values.documents!).then(processedFiles => {
               ndfAddDepenseSimple(context.ndfId, { ...values, documents: processedFiles })
                    .then(r => {
                         queryClient.setQueryData(["ndfDetails", context.ndfId], (details: IConsultantNdfDetailModel[] | undefined) => {
                              if (details) {
                                   const dataWithNewDepense = [r.data, ...details]
                                   context.computeTotalExpenses(dataWithNewDepense || [])
                                   return dataWithNewDepense
                              }
                              return []
                         })

                         helpers.setSubmitting(false)

                         if (actionAfterSubmitForm === "close") {
                              toast.success("Votre justificatif a bien été ajouté.")
                              context.setIsAddDetailsModalShown(false)
                              context.setTypeAddDetail(null)
                              context.setIsCardDetailSimpleCollapsed(false)
                         } else {
                              toast.success("Votre justificatif a bien été ajouté. Vous pouvez continuer à ajouter un autre si vous le souhaitez.")
                              helpers.resetForm()
                         }
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data

                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                         }

                         if (error?.code === "REGULARATION_IS_REQUIRED") {
                              helpers.setStatus(
                                   <>
                                        <p>{error?.detail}</p>
                                        <b className={"mb-2 fs-5"}>Merci de suivre les étapes suivantes:</b>
                                        <ul>
                                             <li>Accédez à la liste des déclarations.</li>
                                             <li>Demandez la régularisation de la déclaration du mois concerné.</li>
                                             <li>Attendez la validation de votre responsable (utilisez ce temps pour finaliser la déclaration du mois en cours).</li>
                                             <li>Une fois la demande validée, ajoutez votre dépense et vérifiez qu'il n'y a plus de ticket restaurant.</li>
                                             <li>Finaliser et signer votre déclaration.</li>
                                        </ul>
                                   </>
                              )
                         } else {
                              helpers.setStatus(error?.detail)
                         }
                         toast.error(error?.detail, { autoClose: false })
                         helpers.setSubmitting(false)
                    })
          })
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
               {helpers => {
                    const isKm = helpers.values.categorie === CONST_NDF_DETAILS_CATEGORIE_KM
                    const showCommentAndSubmit = !isKm || (isKm && !!helpers.values.moisAnneeKm)

                    return (
                         <Form noValidate autoComplete="off">
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              <div className="row g-9">
                                   {/* COLONNE GAUCHE (ou en-dessous sur mobile) : LE FORMULAIRE DE DONNÉES */}
                                   <div className={clsx("col-lg-7 col-xl-6 order-2 order-lg-1", !helpers.values.categorie && "mx-auto")}>
                                        <div className="d-flex flex-column gap-4">
                                             <NdfBasicFieldsSection helpers={helpers} validationSchema={validationSchema} />

                                             {helpers.values.categorie && (
                                                  <div className="animate__animated animate__fadeIn">
                                                       <div className="my-4 separator separator-dashed" />

                                                       <NdfMealSection helpers={helpers} validationSchema={validationSchema} />

                                                       {!isKm && (
                                                            <MyInputFormField
                                                                 formikProps={helpers}
                                                                 validationSchema={validationSchema}
                                                                 name={"montantTTC"}
                                                                 label={<div className={"required"}>Montant TTC</div>}
                                                                 inputMode={"decimal"}
                                                                 type={"number"}
                                                                 placeholder={"Montant TTC"}
                                                                 classNameLabel={"col-lg-4"}
                                                                 classNameField={"col-lg-8"}
                                                                 classNameLayout={"mb-2"}
                                                            />
                                                       )}

                                                       <NdfMileageSection
                                                            helpers={helpers}
                                                            validationSchema={validationSchema}
                                                            detailsIndemniteKm={detailsIndemniteKm}
                                                            setDetailsIndemniteKm={setDetailsIndemniteKm}
                                                       />

                                                       {showCommentAndSubmit && (
                                                            <div className="animate__animated animate__fadeIn">
                                                                 <NdfCommentSection helpers={helpers} validationSchema={validationSchema} />
                                                                 <div className="separator separator-dashed my-6" />
                                                                 <NdfSubmitActions helpers={helpers} setSubmitAction={setActionAfterSubmitForm} />
                                                            </div>
                                                       )}
                                                  </div>
                                             )}
                                        </div>
                                   </div>

                                   {/* COLONNE DROITE (ou au-dessus sur mobile) : LES JUSTIFICATIFS */}
                                   {/* MODIFICATION ICI : On affiche la section même si c'est une IK (!isKm supprimé) */}
                                   {helpers.values.categorie && (
                                        <div className="col-lg-5 col-xl-4 order-1 order-lg-2 mb-8 mb-lg-0 animate__animated animate__fadeInRight">
                                             <div className="position-sticky" style={{ top: "25px", zIndex: 1 }}>
                                                  <NdfJustificatifsSection helpers={helpers} />
                                             </div>
                                        </div>
                                   )}
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}
